import React, { useEffect, useState } from 'react';
import { ModalDialog } from '../../../components/common/Dialogs';
import { useTrans } from '../../../services/i18n';
import useBooleanHandlers from '../../../hooks/useBooleanHandlers';
import { Button } from '../../../components/common/Buttons';
import Typography from '../../../components/common/Typography';
import { AmplifyAuth } from '../../../services/amplifyAuth';
import { SpacingContainer } from '../../../components/common/Containers/Base';
import { List, ListItem, ListItemText, makeStyles } from '@material-ui/core';
import { Divider } from '../../../components/common/Divider';
import MessageIcon from '@material-ui/icons/Message';
import CropFreeIcon from '@material-ui/icons/CropFree';
import SmsFa from './SmsFa';
import AuthenticatorAppFa from './AuthenticatorAppFa';
import SuccessMfa from './SuccessMfa';
import CodeInput from '../../../components/common/Inputs/CodeInput';
import { Grid } from '@material-ui/core';
import { useAlertContext } from '../../../contexts/AlertContext';

const AUTH_NOMFA = 'NOMFA';
const AUTH_SMS_MFA = 'SMS_MFA';
const AUTH_TOTP = 'TOTP';
const AUTH_SOFTWARE_TOKEN_MFA = 'SOFTWARE_TOKEN_MFA';

const useStyles = makeStyles((theme) => ({
  containerCard: {
    padding: '2.125rem 1.5rem 0.5rem',
    display: 'flex',
    flexDirection: 'column',
  },
  iconColor: {
    color: theme.palette.primary.main,
  },
  marginLeft: {
    marginLeft: '0.5rem',
  },
}));

const MFA_METHOD = {
  SMS: AUTH_SMS_MFA,
  APP: [AUTH_SOFTWARE_TOKEN_MFA, AUTH_TOTP],
};

function getPreferredMFA(auth = {}) {
  if (auth.preferredMFA) {
    return auth.preferredMFA;
  }

  const twoFaActive = auth['2faActive'];

  if (!twoFaActive) {
    return AUTH_NOMFA;
  }

  const normalized = String(twoFaActive).toLowerCase();

  if (normalized === 'sms_mfa' || normalized === 'sms') {
    return AUTH_SMS_MFA;
  }

  if (normalized === 'totp') {
    return AUTH_TOTP;
  }

  if (normalized === 'software_token_mfa') {
    return AUTH_SOFTWARE_TOKEN_MFA;
  }

  return AUTH_NOMFA;
}

export default function TwoFactorAuthentication({ auth = {}, onUpdate, updatePhone }) {
  const { trans } = useTrans();
  const { phone } = auth;
  const preferredMFA = getPreferredMFA(auth);
  const mfaEnabled = preferredMFA !== AUTH_NOMFA;
  const [open, onOpenModal, onCloseModal] = useBooleanHandlers();
  const [qrCode, setQrCode] = React.useState(undefined);
  const [twoFaType, setTwoFaType] = React.useState(null);
  const [pendingDisabled, setPendingDisabled] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [openTwoFactorModal, setOpenTwoFactorModal] = useState(false);
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const { successAlert, errorAlert, infoAlert } = useAlertContext();
  const [pendingSendCode, setPendingSendCode] = useState(false);
  const styles = useStyles();

  const onOpen = React.useCallback(() => {
    if (!mfaEnabled) {
      AmplifyAuth.currentAuthenticatedUser()
        .then((user) => {
          const username = user.attributes?.preferred_username || user.username;
          const issuer = 'USFCR Admin';

          return AmplifyAuth.setupTOTP(user).then((secretCode) =>
            setQrCode(`otpauth://totp/${username}?secret=${secretCode}&issuer=${issuer}`)
          );
        })
        .catch(console.warn);
    }

    return onOpenModal();
  }, [mfaEnabled, onOpenModal]);

  const onDisable = () => {
    setPendingDisabled(true);

    AmplifyAuth.currentAuthenticatedUser()
      .then((user) => AmplifyAuth.setPreferredMFA(user, AUTH_NOMFA))
      .then(() => {
        return onUpdate(AUTH_NOMFA);
      })
      .then(() => {
        setOpenTwoFactorModal(false);
        setPending(false);
        setCode('');
        successAlert(trans('two-factor-disabled-successfully'));
      })
      .finally(() => {
        setPendingDisabled(false);
        setPending(false);
      })
      .catch((e) => {
        setPendingDisabled(false);
        setPending(false);
        errorAlert(e.message);
      });
  };

  const onMfaSubmit = () => {
    setPending(true);

    if (preferredMFA === MFA_METHOD.SMS) {
      AmplifyAuth.verifyCurrentUserAttributeSubmit('phone_number', code)
        .then(() => {
          onDisable();
        })
        .catch(() => {
          setPending(false);
          errorAlert(trans('incorrect-authentication-code'));
        });

      return;
    }

    AmplifyAuth.currentAuthenticatedUser()
      .then((user) => {
        return AmplifyAuth.verifyTotpToken(user, code)
          .then(() => {
            onDisable();
          })
          .catch(() => {
            setPending(false);
            errorAlert(trans('incorrect-authentication-code'));
          });
      })
      .catch((e) => {
        setPending(false);
        errorAlert(e.message);
      });
  };

  const resendCode = () => {
    if (preferredMFA === MFA_METHOD.SMS) {
      setPendingSendCode(true);

      AmplifyAuth.verifyCurrentUserAttribute('phone_number')
        .then(() => {
          successAlert(trans('we-sent-you-a-code'));
        })
        .catch((e) => {
          errorAlert(e.message);
        })
        .finally(() => {
          setPendingSendCode(false);
        });
    }
  };

  const showModalDisabled = () => {
    setOpenTwoFactorModal(true);
    resendCode();
  };

  const handleCancel = () => {
    onCloseModal();
    setTwoFaType(null);
    setSuccess(false);
  };

  const onSuccess = () => {
    setTwoFaType(null);
    setSuccess(true);
  };

  const onOpenEnable = (method) => {
    if (mfaEnabled) {
      if (method === 'app' && !MFA_METHOD.APP.includes(preferredMFA)) {
        infoAlert(trans('disable-sms'));
        return;
      }

      if (method === 'sms' && preferredMFA !== MFA_METHOD.SMS) {
        infoAlert(trans('disable-app'));
        return;
      }
    }

    setTwoFaType(method);
    onOpen();
  };

  const onOpenDisable = () => {
    showModalDisabled();
  };

  useEffect(() => {
    if (twoFaType === 'app') {
      onOpen();
    }
  }, [twoFaType, onOpen]);

  return (
    <React.Fragment>
      <Grid container spacing={3} className={styles.containerCard}>
        <Grid item lg={12} md={12} xs={12}>
          <List>
            <ListItem disableGutters>
              <MessageIcon color="primary" />

              <ListItemText className={styles.marginLeft}>
                <Typography variant="h6" component="p" weight="600">
                  {trans('sms')}
                </Typography>
              </ListItemText>

              {mfaEnabled && preferredMFA === MFA_METHOD.SMS ? (
                <Button color="error" variant="outlined" onClick={() => onOpenDisable('sms')}>
                  {trans('disable')}
                </Button>
              ) : (
                <Button color="success" variant="outlined" onClick={() => onOpenEnable('sms')}>
                  {trans('enable')}
                </Button>
              )}
            </ListItem>

            <Divider light spacing={1} />

            <ListItem disableGutters>
              <CropFreeIcon className={styles.iconColor} />

              <ListItemText className={styles.marginLeft}>
                <Typography variant="h6" component="p" weight="600">
                  {trans('authenticator-app')}
                </Typography>
              </ListItemText>

              {mfaEnabled && MFA_METHOD.APP.includes(preferredMFA) ? (
                <Button color="error" variant="outlined" onClick={() => onOpenDisable('app')}>
                  {trans('disable')}
                </Button>
              ) : (
                <Button color="success" variant="outlined" onClick={() => onOpenEnable('app')}>
                  {trans('enable')}
                </Button>
              )}
            </ListItem>
          </List>
        </Grid>
      </Grid>

      <ModalDialog open={open} onClose={handleCancel} title={trans('two-factor-authentication')}>
        <SpacingContainer py={0}>
          {twoFaType === 'sms' && !success && (
            <SmsFa userPhone={phone} updateMFA={onUpdate} updatePhone={updatePhone} setSuccess={onSuccess} />
          )}

          {twoFaType === 'app' && !success && (
            <AuthenticatorAppFa qrCode={qrCode} updateMFA={onUpdate} setSuccess={onSuccess} />
          )}

          {success && <SuccessMfa type={twoFaType} />}
        </SpacingContainer>
      </ModalDialog>

      <ModalDialog
        open={openTwoFactorModal}
        onClose={() => setOpenTwoFactorModal(false)}
        title={trans('two-factor-authentication')}
      >
        <Grid container direction="column" alignItems="center">
          <CodeInput type="number" onChange={setCode} />

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography color="hint" align="center" weight="600" variant="body2" gutter="verticalSpaceBig">
              {preferredMFA === MFA_METHOD.SMS
                ? trans('enter-code-displayed-in-sms')
                : trans('enter-code-displayed-in-application')}
            </Typography>

            {preferredMFA === MFA_METHOD.SMS && (
              <Button
                style={{ marginLeft: '16px' }}
                variant="outlined"
                color="primary"
                size="small"
                pending={pendingSendCode}
                onClick={resendCode}
              >
                {trans('resend-code')}
              </Button>
            )}
          </div>

          <Button
            id="code-confirm"
            variant="contained"
            color="primary"
            size="large"
            onClick={onMfaSubmit}
            disabled={code.length !== 6 || pendingDisabled}
            pending={pending}
          >
            {trans('confirm')}
          </Button>
        </Grid>
      </ModalDialog>
    </React.Fragment>
  );
}